/**
 * Favorites (wishlist): localStorage-backed, opens in a slide-in drawer like
 * the cart. Heart toggles on product cards / the PDP store a minimal product
 * snapshot so the drawer renders without extra fetches.
 */
document.addEventListener('DOMContentLoaded', () => {
  const KEY = 'viva:favorites';
  const commerceEnabled = document.documentElement.dataset.vivaCommerce === 'true';
  const drawer = document.querySelector('[data-viva-fav-drawer]');
  let content = {};
  try { content = JSON.parse(drawer ? drawer.dataset.content || '{}' : '{}'); } catch (error) {}
  const shopUrl = drawer ? drawer.getAttribute('data-shop-url') || '/collections/doczepiane-wlosy' : '/collections/doczepiane-wlosy';

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (error) {
      return [];
    }
  };
  const write = (items) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (error) {
      /* ignore quota / private mode */
    }
  };

  const has = (items, handle) => items.some((i) => i.handle === handle);

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]));

  // --- Toggle buttons reflect saved state ---
  const toggles = () => document.querySelectorAll('[data-fav-toggle]');

  const syncToggles = () => {
    const items = read();
    toggles().forEach((btn) => {
      const active = has(items, btn.getAttribute('data-handle'));
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.classList.toggle('is-active', active);
    });
  };

  const updateBadges = (count) => {
    document.querySelectorAll('[data-viva-fav-toggle]').forEach((link) => {
      let badge = link.querySelector('.viva-cart-badge');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'viva-cart-badge';
          link.appendChild(badge);
        }
        badge.textContent = String(count);
      } else if (badge) {
        badge.remove();
      }
    });
  };

  // --- Drawer ---
  let lastFocus = null;

  const renderDrawer = () => {
    if (!drawer) return;
    const body = drawer.querySelector('[data-viva-fav-body]');
    const foot = drawer.querySelector('[data-viva-fav-foot]');
    const items = read();
    updateBadges(items.length);
    if (!body) return;
    if (!items.length) {
      body.innerHTML =
        `<div class="viva-fav-drawer__empty"><p>${escapeHtml(content.fav_empty)}</p>` +
        `<a class="viva-button viva-button--primary" href="${escapeHtml(shopUrl)}">${escapeHtml(content.drawer_shop)}</a></div>`;
      if (foot) foot.hidden = true;
      return;
    }
    body.innerHTML = `<ul class="viva-fav-drawer__list">${items
      .map((item) => {
        const image = item.image
          ? `<img class="viva-fav-line__image" src="${escapeHtml(item.image)}" alt="" width="64" height="80" loading="lazy">`
          : '<span class="viva-fav-line__image viva-fav-line__image--empty" aria-hidden="true"></span>';
        return `
        <li class="viva-fav-line" data-handle="${escapeHtml(item.handle)}">
          <a class="viva-fav-line__media" href="${escapeHtml(item.url)}">${image}</a>
          <div class="viva-fav-line__info">
            <a class="viva-fav-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
            ${item.variantTitle ? `<span class="viva-fav-line__variant">${escapeHtml(item.variantTitle)}</span>` : ''}
            ${commerceEnabled && item.price ? `<span class="viva-fav-line__price">${escapeHtml(item.price)}</span>` : ''}
          </div>
          <button class="viva-fav-line__remove" type="button" data-fav-remove aria-label="${escapeHtml(content.fav_remove)}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12"></path><path d="M18 6 6 18"></path></svg>
          </button>
        </li>`;
      })
      .join('')}</ul>`;
    if (foot) foot.hidden = false;
  };

  const openDrawer = () => {
    if (!drawer) return;
    document.dispatchEvent(new CustomEvent('viva:drawer-open', { detail: 'favorites' }));
    lastFocus = document.activeElement;
    renderDrawer();
    drawer.setAttribute('data-open', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('viva-cart-open');
    const close = drawer.querySelector('[data-viva-fav-close]');
    if (close instanceof HTMLElement) close.focus();
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('viva-cart-open');
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  };

  document.addEventListener('viva:drawer-open', (event) => {
    if (event.detail !== 'favorites') closeDrawer();
  });

  if (drawer) {
    drawer.querySelectorAll('[data-viva-fav-close]').forEach((el) =>
      el.addEventListener('click', closeDrawer)
    );
    const body = drawer.querySelector('[data-viva-fav-body]');
    if (body) {
      body.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const remove = target.closest('[data-fav-remove]');
        if (!remove) return;
        const line = remove.closest('[data-handle]');
        if (!line) return;
        const handle = line.getAttribute('data-handle');
        write(read().filter((i) => i.handle !== handle));
        renderDrawer();
        syncToggles();
      });
    }
  }

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const trapTab = (event) => {
    if (!drawer) return;
    const items = Array.from(drawer.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  document.addEventListener('keydown', (event) => {
    if (!drawer || drawer.getAttribute('data-open') !== 'true') return;
    if (event.key === 'Escape') closeDrawer();
    else if (event.key === 'Tab') trapTab(event);
  });

  // --- Header favorites icon opens the drawer ---
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-viva-fav-toggle]')) return;
    event.preventDefault();
    openDrawer();
  });
  document.addEventListener('shopify:section:load', () => {
    syncToggles();
    updateBadges(read().length);
  });

  // --- Heart toggles on products ---
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const btn = target.closest('[data-fav-toggle]');
    if (!btn) return;
    event.preventDefault();
    const handle = btn.getAttribute('data-handle');
    if (!handle) return;
    const items = read();
    if (has(items, handle)) {
      write(items.filter((i) => i.handle !== handle));
    } else {
      items.push({
        handle,
        url: btn.getAttribute('data-url') || '',
        title: btn.getAttribute('data-title') || '',
        variantTitle: btn.getAttribute('data-variant-title') || '',
        price: btn.getAttribute('data-price') || '',
        image: btn.getAttribute('data-image') || '',
      });
      write(items);
    }
    syncToggles();
    if (drawer && drawer.getAttribute('data-open') === 'true') renderDrawer();
    updateBadges(read().length);
  });

  syncToggles();
  updateBadges(read().length);
});
